import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "../../lib/utils";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => {
  // Radix Dialog's modal content sets `disableOutsidePointerEvents: true`,
  // which blocks pointer events on any DOM node outside DialogContent —
  // including this popover's own content, since Popover.Portal renders to
  // document.body by default, making it a DOM *sibling* of the dialog
  // rather than a descendant. Clicks/typing inside the popover then don't
  // register at all, and the click falls through to the dialog overlay
  // beneath, which reads as an outside click and closes things. Portaling
  // into the open dialog's own content node (when one exists) keeps the
  // popover inside the subtree Radix already exempts from that block.
  const [container, setContainer] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    // Radix portals append to document.body in mount order, so the last
    // match is the most-recently-opened (i.e. topmost/innermost) dialog —
    // relevant if dialogs are ever stacked.
    const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"]');
    setContainer(dialogs.length > 0 ? dialogs[dialogs.length - 1] : null);
  }, []);

  return (
    <PopoverPrimitive.Portal container={container ?? undefined}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        // Once portaled inside the dialog's own (scrollable, size-clamped)
        // content box, the popover can still render past that box's edges
        // and get visually clipped by its overflow. Constraining collision
        // detection to that same container makes Radix flip/shift/clamp
        // the popover to stay fully inside it, instead of overflowing.
        collisionBoundary={container ?? undefined}
        collisionPadding={8}
        className={cn(
          "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent };
